/**
 * Eine gemeinsame Quelle fuer die Mitteilungen (Tabelle `notifications`).
 *
 * Bis hierher lud jede Ansicht selbst: App fuer den Zaehler, das Dashboard
 * fuer seine Spalte, die Aufgaben fuer ihren Reiter. Folge: wer eine
 * Mitteilung im Dashboard als gelesen markierte, sah den roten Punkt in der
 * Handy-Navigation weiter leuchten — der Zaehler wurde nur beim Anmelden
 * gerechnet. Jetzt haelt dieser Speicher EINEN Stand, und jede Ansicht liest
 * ihn ueber `useNotifications`.
 *
 * Bewusst ohne React: der Zustand und seine Uebergaenge sind reine Funktionen
 * und damit ohne DOM testbar (das Projekt hat kein jsdom).
 */
import { fetchMyNotifications, markNotificationAsRead } from './cloudService';

export interface AppNotification {
    id: string;
    project_id: string | null;
    project_name: string | null;
    sender_id: string | null;
    sender_name: string | null;
    recipient_id: string;
    content: string;
    /** Heute immer 'mention'; Freigaben und Fremdzugriffe laufen unter demselben Typ. */
    type: string;
    status: 'unread' | 'read' | string;
    original_note_id: string | null;
    created_at: string;
}

export interface NotificationState {
    userId: string | null;
    items: AppNotification[];
    /** Erster Ladevorgang laeuft (spaetere Aktualisierungen zeigen den alten Stand weiter). */
    loading: boolean;
    /** Letzter erfolgreicher Abruf, ms seit Epoche. */
    loadedAt: number | null;
    /** Letzter Abruf ist gescheitert; der alte Stand bleibt stehen. */
    error: boolean;
}

export const EMPTY_NOTIFICATION_STATE: NotificationState = {
    userId: null,
    items: [],
    loading: false,
    loadedAt: null,
    error: false,
};

// ─── Reine Uebergaenge ────────────────────────────────────────────────────

export const isUnread = (n: Pick<AppNotification, 'status'>): boolean => n.status === 'unread';

export const unreadNotifications = (items: ReadonlyArray<AppNotification>): AppNotification[] =>
    items.filter(isUnread);

/** Markiert die genannten Kennungen als gelesen; alles andere bleibt zeichengleich. */
export const withRead = (items: ReadonlyArray<AppNotification>, ids: ReadonlyArray<string>): AppNotification[] => {
    if (ids.length === 0) return [...items];
    const set = new Set(ids);
    return items.map(n => (set.has(n.id) && isUnread(n) ? { ...n, status: 'read' } : n));
};

/** Neueste zuerst; ohne lesbares Datum ans Ende. */
export const sortNewestFirst = (items: ReadonlyArray<AppNotification>): AppNotification[] => {
    const t = (n: AppNotification) => {
        const v = Date.parse(n.created_at);
        return Number.isFinite(v) ? v : -Infinity;
    };
    return [...items].sort((a, b) => t(b) - t(a));
};

/**
 * Wie oft ein Abruf hoechstens laeuft, egal wie viele Ansichten gleichzeitig
 * „aktualisieren" rufen (Fokus, Sichtbarkeit, Intervall, Mount).
 */
export const MIN_REFRESH_GAP_MS = 10_000;

export const shouldRefresh = (state: NotificationState, userId: string, nowMs: number, force = false): boolean => {
    if (force) return true;
    if (state.userId !== userId) return true;
    if (state.loadedAt === null) return true;
    return nowMs - state.loadedAt >= MIN_REFRESH_GAP_MS;
};

// ─── Speicher ─────────────────────────────────────────────────────────────

let state: NotificationState = EMPTY_NOTIFICATION_STATE;
const listeners = new Set<() => void>();

const setState = (next: NotificationState) => {
    state = next;
    listeners.forEach(fn => fn());
};

export const getNotificationState = (): NotificationState => state;

export const subscribeNotifications = (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
};

let inflight: { userId: string; promise: Promise<void> } | null = null;

/**
 * In dieser Sitzung als gelesen markierte Kennungen. Ein Abruf, der VOR der
 * Quittung losging und erst danach ankommt, meldet sie noch als ungelesen —
 * ohne diese Menge tauchte die eben quittierte Mitteilung wieder auf
 * (Codex-Review 25.09.2026). Scheitert die Quittung, fliegt die Kennung
 * wieder heraus.
 */
let quittiert = new Set<string>();

/**
 * Laedt die Mitteilungen des Nutzers. Laeuft schon ein Abruf fuer denselben
 * Nutzer, wird auf diesen gewartet statt einen zweiten zu schicken.
 */
export const refreshNotifications = (userId: string, opts: { force?: boolean } = {}): Promise<void> => {
    if (!userId) return Promise.resolve();
    if (inflight && inflight.userId === userId) return inflight.promise;
    if (!shouldRefresh(state, userId, Date.now(), opts.force)) return Promise.resolve();

    const userChanged = state.userId !== userId;
    setState({
        ...(userChanged ? EMPTY_NOTIFICATION_STATE : state),
        userId,
        loading: userChanged || state.loadedAt === null,
    });

    const promise = (async () => {
        try {
            const data = (await fetchMyNotifications(userId)) as AppNotification[];
            // Nutzer inzwischen gewechselt (Abmelden/Anmelden im Abruf): verwerfen.
            if (state.userId !== userId) return;
            setState({ userId, items: withRead(sortNewestFirst(data || []), [...quittiert]), loading: false, loadedAt: Date.now(), error: false });
        } catch (e) {
            console.error('[notifications] Abruf fehlgeschlagen', e);
            if (state.userId !== userId) return;
            setState({ ...state, loading: false, error: true });
        } finally {
            if (inflight?.promise === promise) inflight = null;
        }
    })();
    inflight = { userId, promise };
    return promise;
};

/**
 * Als gelesen markieren — sofort im Speicher (der Zaehler faellt ohne
 * Wartezeit), danach am Server. Scheitert der Server, wird der Stand neu
 * geladen statt geraten.
 */
export const markNotificationsRead = async (ids: ReadonlyArray<string>): Promise<void> => {
    const offen = ids.filter(id => state.items.some(n => n.id === id && isUnread(n)));
    if (offen.length === 0) return;
    offen.forEach(id => quittiert.add(id));
    setState({ ...state, items: withRead(state.items, offen) });
    const ergebnisse = await Promise.allSettled(offen.map(id => markNotificationAsRead(id)));
    const gescheitert = offen.filter((_, i) => ergebnisse[i].status === 'rejected');
    if (gescheitert.length > 0) {
        console.error('[notifications] Als gelesen markieren fehlgeschlagen', gescheitert.length);
        gescheitert.forEach(id => quittiert.delete(id));
        if (state.userId) await refreshNotifications(state.userId, { force: true });
        throw new Error('Mitteilung konnte nicht als gelesen markiert werden.');
    }
};

/** Beim Abmelden: nichts vom vorigen Nutzer stehen lassen. */
export const resetNotifications = (): void => {
    inflight = null;
    quittiert = new Set<string>();
    setState(EMPTY_NOTIFICATION_STATE);
};
